import React from 'react'
import {Box, Tooltip} from '@mui/material'

export interface StatusBadgeProps {
  status: 'new' | 'alpha' | 'beta' | 'coming-soon' | 'community'
  size?: 'small' | 'medium'
  variant?: 'filled' | 'outlined'
  compact?: boolean // For sidebar usage
  tooltip?: string
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'small',
  variant = 'filled',
  compact = false,
  tooltip
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'new':
        return {
          color: '#008FA8', // cyan-600 — brand primary
          backgroundColor: variant === 'filled' ? '#008FA8' : 'rgba(0, 143, 168, 0.08)',
          text: 'NEW'
        }
      case 'alpha':
        return {
          color: '#3A6E8F', // info — warm slate-blue
          backgroundColor: variant === 'filled' ? '#3A6E8F' : 'rgba(58, 110, 143, 0.08)',
          text: 'ALPHA'
        }
      case 'beta':
        return {
          color: '#2E8B5C', // success — warm sage-green
          backgroundColor: variant === 'filled' ? '#2E8B5C' : 'rgba(46, 139, 92, 0.08)',
          text: 'BETA'
        }
      case 'coming-soon':
        return {
          color: '#B27A28', // warning — deeper ochre
          backgroundColor: variant === 'filled' ? '#B27A28' : 'rgba(178, 122, 40, 0.08)',
          text: compact ? 'SOON' : 'COMING SOON'
        }
      case 'community':
        return {
          // Community = neutral stone, NOT purple. Purple is reserved exclusively for AI.
          color: '#57524a', // stone-600
          backgroundColor: variant === 'filled' ? '#57524a' : 'rgba(87, 82, 74, 0.08)',
          text: compact ? 'CMTY' : 'COMMUNITY'
        }
    }
  }

  const config = getStatusConfig()
  const isSmall = size === 'small'
  const isFilled = variant === 'filled'

  const badge = (
    <Box
      sx={{
        display: 'inline-block',
        bgcolor: config.backgroundColor,
        color: isFilled ? 'white' : config.color,
        px: isSmall ? 1 : 1.5,
        py: isSmall ? 0.25 : 0.5,
        borderRadius: 1,
        fontSize: isSmall ? '0.65rem' : '0.75rem',
        fontWeight: 'bold',
        border: isFilled ? 'none' : `1px solid ${config.color}`,
        textAlign: 'center',
        minWidth: isSmall ? '35px' : '45px',
        boxShadow: isFilled ? 1 : 0,
        transition: 'all 0.2s ease-in-out'
      }}
    >
      {config.text}
    </Box>
  )

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {badge}
    </Tooltip>
  ) : (
    badge
  )
}

export default StatusBadge